import HappyLine from 'js/components/HappyLine/HappyLine'
import React, { Fragment } from 'react'
import { Link } from 'react-router-dom'
import Logo from '../../components/Logo'
import messages from './messages'
import { FormattedMessage } from 'react-intl'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { trackViewFaq } from '../../analytics'
import { useInView } from 'react-intersection-observer'
import useLocale from '../../hooks/useLocale'
import loadable from '@loadable/component'

const AppButtons = loadable(() => import('../AppButtons/AppButtons'))

const Heading = props => (
  <h3
    className="mb-8 type-14-demibold-caps lg:type-17-demibold-caps xxl:type-20-demibold-caps"
    {...props}
  />
)

const ListItem = ({ link = false, href, ...props }) => {
  const className =
    'text-grey-darkest mb-8 no-underline transition hover:text-blue'

  if (link) {
    return (
      <li>
        <Link className={className} to={href} {...props} />
      </li>
    )
  } else {
    return (
      <li>
        <a className={className} href={href} {...props} />
      </li>
    )
  }
}

function Footer(props) {
  const [inViewRef, inView] = useInView({ threshold: 0.8, triggerOnce: true })
  const locale = useLocale()
  const {
    appButtons = true,
    flat,
    happyLine = true,
    marginless,
    roomForContent,
    showCareers = true,
  } = props
  const hidden = locale.language !== 'en'

  return (
    <footer
      ref={inViewRef}
      className={classNames('relative text-grey-darkest', {
        'mt-64 md:mt-96 lg:mt-128 xl:mt-192': !(marginless || flat),
      })}
    >
      {happyLine && !flat && (
        <HappyLine style={{ marginTop: '-4.19921875vw' }} />
      )}
      <div
        className={classNames('bg-white pt-48 pb-16', {
          'lg:pt-128': marginless || roomForContent === 'small',
          'md:pt-192': roomForContent === true,
        })}
      >
        <div className="max-w-1680 px-16 md:px-40 lg:px-64 xxl:px-128">
          <Link to="/" className="inline-block mb-24 md:mb-40">
            <span className="visually-hidden">Lifesum.com start page</span>
            <Logo />
          </Link>
          <div className="flex flex-wrap -mx-12 lg:-mx-20 leading-body">
            <h2 className="visually-hidden">Assorted Lifesum links</h2>
            <div className="flex flex-col flex-grow px-12 lg:px-20 w-1/2 md:w-1/3 lg:w-1/4 xl:w-5/24">
              {showCareers && (
                <Fragment>
                  <Heading>
                    <FormattedMessage {...messages.work} />
                  </Heading>
                  <ul className="mb-24 md:mb-40">
                    <ListItem href="http://jobs.lifesum.com">
                      <FormattedMessage {...messages.careers} />
                    </ListItem>
                  </ul>
                </Fragment>
              )}
              <Heading>
                <FormattedMessage {...messages.connect} />
              </Heading>
              <ul className="mb-24 md:mb-40">
                <ListItem href="https://instagram.com/lifesum/">
                  Instagram
                </ListItem>
                <ListItem href="https://www.youtube.com/channel/UCy64gY3TCURHxigExH0cHIg">
                  YouTube
                </ListItem>
                <ListItem href="https://www.pinterest.com/Lifesum/">
                  Pinterest
                </ListItem>
                <ListItem href="https://www.facebook.com/Lifesum">
                  Facebook
                </ListItem>
                <ListItem href="https://twitter.com/lifesum">Twitter</ListItem>

                <ListItem href="http://blog.lifesum.com/">
                  <FormattedMessage {...messages.blog} />
                </ListItem>
              </ul>
            </div>
            <div className="flex flex-col flex-grow px-12 lg:px-20 w-1/2 md:w-1/3 lg:w-1/4 xl:w-5/24">
              <Heading>
                <FormattedMessage {...messages.help} />
              </Heading>
              <ul className="mb-24 md:mb-40">
                <ListItem
                  href="https://lifesum.helpshift.com/"
                  onClick={() => trackViewFaq()}
                >
                  <FormattedMessage {...messages.faq} />
                </ListItem>
                <ListItem href="/contact/" link>
                  <FormattedMessage {...messages.contact} />
                </ListItem>
              </ul>
              <Heading>
                <FormattedMessage {...messages.learnMore} />
              </Heading>
              <ul className="mb-24 md:mb-40">
                <ListItem href="/about/" link>
                  <FormattedMessage {...messages.aboutLifesum} />
                </ListItem>
                <ListItem href="/press/" link>
                  <FormattedMessage {...messages.press} />
                </ListItem>
                {!hidden && (
                  <ListItem link href="/health-education/">
                    <FormattedMessage {...messages.healthEdu} />
                  </ListItem>
                )}
              </ul>
            </div>
            <div className="flex flex-col flex-grow px-12 lg:px-20 w-1/2 md:w-1/3 lg:w-1/4 xl:w-5/24">
              <Heading>
                <FormattedMessage {...messages.legal} />
              </Heading>
              <ul className="mb-24 md:mb-40">
                <ListItem
                  link
                  href={locale.language === 'de' ? '/policy-de/' : '/policy/'}
                >
                  <FormattedMessage {...messages.TOSandPrivacyPolicy} />
                </ListItem>
                <ListItem
                  link
                  href={
                    locale.language === 'de'
                      ? '/policy-de/#cookies'
                      : '/policy/#cookies'
                  }
                >
                  <FormattedMessage {...messages.foot_how_we_use_cookies} />
                </ListItem>
              </ul>
              <div ref={inViewRef}>
                {inView && (
                  <img
                    src={require('media/toaster.gif')}
                    alt=""
                    className="c-toaster"
                  />
                )}
              </div>
              <small className="type-14 text-grey">
                <FormattedMessage {...messages.allRightsReserved} /> 2020
                Lifesum AB
              </small>
            </div>
            {Boolean(appButtons) && (
              <div className="flex flex-col flex-grow px-12 lg:px-20 w-1/2 md:w-1/3 lg:w-1/4 xl:w-9/24">
                <div className="mb-40 text-left md:text-center lg:text-right">
                  {inView && <AppButtons />}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  )
}

Footer.propTypes = {
  appButtons: PropTypes.bool,
  marginless: PropTypes.bool,
  happyLine: PropTypes.bool,
  showCareers: PropTypes.bool,
  roomForContent: PropTypes.bool,
  flat: PropTypes.bool,
}

export default Footer
